// MACHINERY V2 (Stroje) — capability-badge tooltip (M2.9).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.9 + §3 (Radix tooltip,
// "CapTip"). Reference impl: design_handoff_machinery_v3/machinery-live.jsx
// (`CapTip`, 188px card) + README.md §8.
//
// TEXT-ONLY BY RULING (review fix 3). This tooltip carries NO interactive
// content — no link, no button. Radix documents that tooltips must not contain
// interactive content (that is Popover's job): a tooltip does not open on
// touch (Radix ignores pointerType "touch") and closes as focus leaves the
// trigger, so anything interactive inside is unreachable for touch AND
// keyboard users. On the phone/tablet tiers this project mandates that made
// the readiness surface a dead end. The DISCLOSURE affordance therefore lives
// on the badge itself (CapabilityBadge opens ReadinessPopupV2 on
// click/tap/Enter/Space); this component is pure hover enrichment.
//
// PORTAL RULE — LIVE HERE, NOT THEORETICAL (MachineryV2Layout.tsx:20-39).
// Radix Tooltip.Content renders through a portal to document.body, OUTSIDE
// `.machinery-v2-root`, where --card / --border / --ink / --faint / --muted
// DO NOT RESOLVE. So the Content below:
//   • re-carries `machinery-v2-root p-<palette> mod-stroje` on itself;
//   • sets inline background:"transparent", because re-carrying the root class
//     also re-applies its opaque `background: var(--bg)` — without the
//     override the tooltip would paint a page-coloured slab behind itself;
//   • paints the real 188px surface on the INNER div;
//   • reads `palette` from machineryV2UiSlice INSIDE this component on EVERY
//     render — never captured at mount, or a tooltip opened before a palette
//     flip would keep the old palette while the page changed.
//
// This DUPLICATES the countermeasure from chrome/ChromeRightMenus.tsx's
// `MenuContent`, which is the CANONICAL implementation. It is not reused
// because `MenuContent` is hard-bound to DropdownMenu.Portal/.Content, and a
// DropdownMenu.Content cannot be rendered inside a Tooltip.Root — the two
// primitives have different context, prop types and required parents. The
// countermeasure itself (class + transparent + live palette read) is copied
// verbatim; if it ever changes, change it in BOTH files and in
// ReadinessPopupV2.tsx.
//
// The TooltipProvider is hoisted once in MachineryV2Layout.tsx — do NOT add a
// provider here (MIDAS shipped a P6 crash hotfix for exactly that).
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";
import * as Tooltip from "@radix-ui/react-tooltip";

import { useAppSelector } from "../../../../shared/hooks/sharedHooks";

export interface CapTipProps {
  /** Mono uppercase eyebrow — the capability name. */
  title: string;
  /** The one-line verdict. */
  line: string;
  /** Optional second line: provenance / how-it-works. */
  hint?: string | null;
  /** The badge (or any element) the tooltip is anchored to. */
  children: React.ReactNode;
}

const CapTip: React.FC<CapTipProps> = ({ title, line, hint = null, children }) => {
  const palette = useAppSelector((s) => s.machineryV2Ui.palette);

  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          className={`machinery-v2-root p-${palette} mod-stroje`}
          side="bottom"
          align="start"
          sideOffset={7}
          collisionPadding={12}
          // The portalled DOM sits under <body>, but React synthetic events
          // still bubble along the COMPONENT tree into the host card's
          // onClick. Without this, a click landing on tooltip content would
          // navigate the card underneath it. (Review fix 2.)
          onClick={(e) => e.stopPropagation()}
          style={{ background: "transparent", zIndex: 1300 }}
        >
          <div
            style={{
              width: 188,
              background: "var(--card)",
              border: "1px solid var(--border)",
              borderRadius: 8,
              boxShadow:
                "0 8px 24px rgba(15, 23, 30, 0.14), 0 2px 6px rgba(15, 23, 30, 0.08)",
              padding: "8px 11px 9px",
              display: "flex",
              flexDirection: "column",
              gap: 3,
              textAlign: "left",
              cursor: "default",
            }}
          >
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 9,
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "var(--muted)",
              }}
            >
              {title}
            </span>
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "var(--ink)",
                lineHeight: 1.4,
              }}
            >
              {line}
            </span>
            {hint && (
              <span style={{ fontSize: 10.5, color: "var(--faint)", lineHeight: 1.45 }}>
                {hint}
              </span>
            )}
          </div>
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
};

export default CapTip;
